import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import openPdf from '../../api/openPdf';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useAuth } from '../../context/AuthContext';
import BudgetVersions from '../../components/budgets/BudgetVersions';

const statusLabels = {
  draft:     { label: 'Borrador',  color: 'badge-secondary' },
  sent:      { label: 'Enviado',   color: 'badge-info' },
  accepted:  { label: 'Aceptado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const paymentMethodLabels = {
  cash: 'Efectivo', transfer: 'Transferencia', check: 'Cheque', card: 'Tarjeta', other: 'Otro',
};

const paymentStatusLabels = {
  pending:   { label: 'Pendiente',  color: 'badge-warning' },
  validated: { label: 'Validado',   color: 'badge-success' },
  rejected:  { label: 'Rechazado',  color: 'badge-danger' },
};

const emptyPayment = {
  amount: '', currency: 'mxn', payment_method: 'transfer',
  reference: '', payment_date: new Date().toISOString().slice(0, 10), notes: '',
};

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

const ConfirmModal = ({ show, title, message, onConfirm, onCancel, confirmLabel = 'Eliminar', danger = true, loading = false }) => {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: danger ? '#FEE2E2' : '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <svg style={{ width: 24, height: 24, color: danger ? '#EF4444' : '#F59E0B' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h6 style={{ marginBottom: '8px' }}>{title}</h6>
          <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: 0 }}>{message}</p>
        </div>
        <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={loading}>Cancelar</button>
          <button className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-warning'}`} onClick={onConfirm} disabled={loading}>
            {loading ? 'Procesando...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const BudgetDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('items');

  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({});
  const [savingItem, setSavingItem] = useState(false);
  const [deleteItemModal, setDeleteItemModal] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  const [showAddItem, setShowAddItem] = useState(null);
  const [newItemForm, setNewItemForm] = useState({ name: '', name_en: '', type: 'normal', amount: 0, includes_iva: false, iva_rate: 16 });

  // Pagos
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [paymentVoucher, setPaymentVoucher] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [deletePaymentModal, setDeletePaymentModal] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [hasUnversionedChanges, setHasUnversionedChanges] = useState(false);

  // Versiones — key para forzar re-render del componente tras restaurar
  const [versionsKey, setVersionsKey] = useState(0);

  const fetchBudget = useCallback(async () => {
    try {
      const res = await api.get(`/budgets/${id}`);
      setBudget(res.data);
      setInfoForm({
        client_name: res.data.client_name || '',
        client_phone: res.data.client_phone || '',
        client_email: res.data.client_email || '',
        client_address: res.data.client_address || '',
        responsible_name: res.data.responsible_name || '',
        language: res.data.language,
        currency: res.data.currency,
        usd_exchange_rate: res.data.usd_exchange_rate,
        show_bank_accounts: res.data.show_bank_accounts,
        notes: res.data.notes || '',
        status: res.data.status,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAuditLog = async () => {
    if (user?.role !== 'notary') return;
    setLoadingAudit(true);
    try {
      const res = await api.get(`/activity-log/budgets/${id}`);
      setAuditLog(res.data);
    } catch (error) { console.error(error); }
    finally { setLoadingAudit(false); }
  };

  useEffect(() => { fetchBudget(); fetchAuditLog(); }, [fetchBudget]);

  // Cuando se restaura una versión, refrescar el presupuesto y el componente de versiones
  const handleVersionRestored = useCallback(() => {
    fetchBudget();
    setVersionsKey(k => k + 1);
  }, [fetchBudget]);

  const fmt = (amount) => {
    const n = Number(amount) || 0;
    if (budget?.currency === 'usd') {
      return `$${(n / (Number(budget.usd_exchange_rate) || 1)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`;
    }
    return `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`;
  };

  const fmtRaw = (amount) => Number(amount).toLocaleString('es-MX', { minimumFractionDigits: 2 });

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.put(`/budgets/${id}`, infoForm);
      toast.success('Presupuesto actualizado');
      setEditingInfo(false);
      // Solo marcar cambios sin versionar si NO cambió el status
      // (el status genera versión automática en backend)
      if (!infoForm.status || infoForm.status === budget.status) {
        setHasUnversionedChanges(true); // ← nuevo
      }
      fetchBudget();
    } catch { toast.error('Error al guardar'); }
    finally { setSavingInfo(false); }
  };

  const handleEditItem = (item) => {
    setEditingItem(item.id);
    setItemForm({ amount: item.amount, includes_iva: item.includes_iva, iva_rate: item.iva_rate, visible: item.visible, name: item.name, name_en: item.name_en || '' });
  };

  const handleSaveItem = async (itemId) => {
    setSavingItem(true);
    try {
      await api.put(`/budgets/${id}/items/${itemId}`, itemForm);
      toast.success('Concepto actualizado');
      setEditingItem(null);
      setHasUnversionedChanges(true); // ← nuevo
      fetchBudget();
    } catch { toast.error('Error al guardar'); }
    finally { setSavingItem(false); }
  };

  const handleDeleteItem = async () => {
    if (!deleteItemModal) return;
    setDeletingItem(true);
    try {
      await api.delete(`/budgets/${id}/items/${deleteItemModal.id}`);
      toast.success('Concepto eliminado');
      setDeleteItemModal(null);
      setHasUnversionedChanges(true); // ← nuevo
      fetchBudget();
    } catch { toast.error('Error al eliminar'); }
    finally { setDeletingItem(false); }
  };

  const handleAddItem = async (e, categoryId) => {
    e.preventDefault();
    try {
      await api.post(`/budgets/${id}/items`, { ...newItemForm, budget_concept_category_id: categoryId });
      toast.success('Concepto agregado');
      setShowAddItem(null);
      setNewItemForm({ name: '', name_en: '', type: 'normal', amount: 0, includes_iva: false, iva_rate: 16 });
      setHasUnversionedChanges(true); // ← nuevo
      fetchBudget();
    } catch { toast.error('Error al agregar'); }
  };

  const handleMoveItem = async (categoryItems, itemId, direction) => {
    const idx = categoryItems.findIndex(i => i.id === itemId);
    const newItems = [...categoryItems];
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === newItems.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    try {
      await api.post(`/budgets/${id}/items/reorder`, { items: newItems.map((item, i) => ({ id: item.id, sort_order: i })) });
      fetchBudget();
    } catch { toast.error('Error al reordenar'); }
  };

  // ── Pagos ──
  const handleEditPayment = (payment) => {
    setEditingPayment(payment.id);
    setPaymentForm({
      amount: payment.amount,
      currency: payment.currency,
      payment_method: payment.payment_method,
      reference: payment.reference || '',
      payment_date: payment.payment_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      notes: payment.notes || '',
    });
    setPaymentVoucher(null);
    setShowPaymentForm(false);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    setSavingPayment(true);
    try {
      const formData = new FormData();
      Object.entries(paymentForm).forEach(([k, v]) => { if (v !== '') formData.append(k, v); });
      if (paymentVoucher) formData.append('voucher', paymentVoucher);

      if (editingPayment) {
        formData.append('_method', 'PUT');
        await api.post(`/budgets/${id}/payments/${editingPayment}`, formData);
        toast.success('Pago actualizado');
        setEditingPayment(null);
        setPaymentForm(emptyPayment);
        setPaymentVoucher(null);
        await fetchBudget();
      } else {
        await api.post(`/budgets/${id}/payments`, formData);
        toast.success('Pago registrado');
        setShowPaymentForm(false);
      }
      setPaymentForm(emptyPayment);
      setPaymentVoucher(null);
      fetchBudget();
    } catch { toast.error('Error al guardar pago'); }
    finally { setSavingPayment(false); }
  };

  const handleValidatePayment = async (paymentId, status) => {
    try {
      await api.put(`/budgets/${id}/payments/${paymentId}/validate`, { status });
      toast.success(status === 'validated' ? 'Pago validado' : 'Pago rechazado');
      fetchBudget();
    } catch { toast.error('Error al validar'); }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentModal) return;
    setDeletingPayment(true);
    try {
      await api.delete(`/budgets/${id}/payments/${deletePaymentModal.id}`);
      toast.success('Pago eliminado');
      setDeletePaymentModal(null);
      fetchBudget();
    } catch { toast.error('Error al eliminar'); }
    finally { setDeletingPayment(false); }
  };

  const handlePrint = (style = 'default') => {
    const lang = budget.language || 'es';
    const currency = budget.currency || 'mxn';
    openPdf(`/api/budgets/${id}/pdf`, { lang, currency, style });
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;
  if (!budget) return <div className="alert alert-danger">Presupuesto no encontrado</div>;

  const allItems = budget.items || [];
  const informativeItems = allItems.filter(i => i.type === 'informative');
  const discountItems = allItems.filter(i => i.type === 'discount');
  const normalItems = allItems.filter(i => i.type === 'normal');

  const categoriesMap = {};
  normalItems.forEach(item => {
    const catId = item.budget_concept_category_id;
    if (!categoriesMap[catId]) {
      categoriesMap[catId] = { id: catId, name: item.concept_category?.name || 'Sin categoría', name_en: item.concept_category?.name_en || '', items: [] };
    }
    categoriesMap[catId].items.push(item);
  });
  const categories = Object.values(categoriesMap);

  const totals = budget.totals || {};
  const grandTotal = totals.grand_total || 0;
  const totalPaid = totals.total_paid || 0;
  const balance = grandTotal - totalPaid;

  const isNotary = user?.role === 'notary';

  const paymentFormJSX = (onCancel) => (
    <div className="border rounded p-3 mb-3 bg-light">
      <h6 className="mb-3">{editingPayment ? 'Editar Pago' : 'Registrar Pago'}</h6>
      <form onSubmit={handleSavePayment}>
        <div className="row">
          <div className="col-12 col-md-4">
            <div className="form-group">
              <label className="small">Monto *</label>
              <input type="number" className="form-control" min="0.01" step="0.01" required
                value={paymentForm.amount}
                onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))} />
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group">
              <label className="small">Forma de pago</label>
              <select className="form-control" value={paymentForm.payment_method}
                onChange={e => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}>
                <option value="transfer">Transferencia</option>
                <option value="cash">Efectivo</option>
                <option value="check">Cheque</option>
                <option value="card">Tarjeta</option>
                <option value="other">Otro</option>
              </select>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <div className="form-group">
              <label className="small">Fecha *</label>
              <input type="date" className="form-control" required
                value={paymentForm.payment_date}
                onChange={e => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))} />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label className="small">Referencia</label>
              <input type="text" className="form-control" placeholder="Número de operación"
                value={paymentForm.reference}
                onChange={e => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))} />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label className="small">
                Comprobante {editingPayment && <span className="text-muted">(dejar vacío para no cambiar)</span>}
              </label>
              <input type="file" className="form-control-file" accept="image/*,application/pdf"
                onChange={e => setPaymentVoucher(e.target.files[0])} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label className="small">Notas</label>
              <textarea className="form-control" rows="2"
                value={paymentForm.notes}
                onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}></textarea>
            </div>
          </div>
        </div>
        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={savingPayment}>
            {savingPayment ? 'Guardando...' : (editingPayment ? 'Actualizar' : 'Registrar')}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );

  const renderItemRow = (item, categoryItems) => (
    <div key={item.id} className="p-3 border-bottom"
      style={{ opacity: item.visible ? 1 : 0.4, backgroundColor: item.visible ? '#fff' : '#f8f9fa' }}>
      {editingItem === item.id ? (
        <div>
          <div className="row mb-2">
            <div className="col-12 col-md-5">
              <input type="text" className="form-control form-control-sm mb-1" placeholder="Nombre ES"
                value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} />
              <input type="text" className="form-control form-control-sm" placeholder="Nombre EN"
                value={itemForm.name_en} onChange={e => setItemForm({ ...itemForm, name_en: e.target.value })} />
            </div>
            <div className="col-12 col-md-4">
              <input type="number" className="form-control form-control-sm mb-1" placeholder="Monto" min="0" step="0.01"
                value={itemForm.amount} onChange={e => setItemForm({ ...itemForm, amount: e.target.value })} />
              {itemForm.includes_iva && (
                <input type="number" className="form-control form-control-sm" placeholder="Tasa IVA %" min="0" max="100" step="0.01"
                  value={itemForm.iva_rate} onChange={e => setItemForm({ ...itemForm, iva_rate: e.target.value })} />
              )}
            </div>
            <div className="col-12 col-md-3">
              <div className="custom-control custom-checkbox mb-1">
                <input type="checkbox" className="custom-control-input" id={`iva-item-${item.id}`}
                  checked={itemForm.includes_iva} onChange={e => setItemForm({ ...itemForm, includes_iva: e.target.checked })} />
                <label className="custom-control-label small" htmlFor={`iva-item-${item.id}`}>IVA</label>
              </div>
              <div className="custom-control custom-checkbox">
                <input type="checkbox" className="custom-control-input" id={`vis-item-${item.id}`}
                  checked={itemForm.visible} onChange={e => setItemForm({ ...itemForm, visible: e.target.checked })} />
                <label className="custom-control-label small" htmlFor={`vis-item-${item.id}`}>Visible</label>
              </div>
            </div>
          </div>
          <div className="d-flex" style={{ gap: '6px' }}>
            <button className="btn btn-primary btn-sm" onClick={() => handleSaveItem(item.id)} disabled={savingItem}>Guardar</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingItem(null)}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-between align-items-start">
          <div style={{ flex: 1 }}>
            <span className="small">{budget.language === 'en' && item.name_en ? item.name_en : item.name}</span>
            {item.includes_iva && <small className="text-muted d-block">+ IVA {item.iva_rate}% = ${fmtRaw(item.iva_amount)}</small>}
          </div>
          <div className="d-flex align-items-center" style={{ gap: '10px' }}>
            <span className={`small font-weight-bold ${item.type === 'discount' ? 'text-danger' : ''}`}>
              {item.type === 'discount' ? '- ' : ''}${fmtRaw(item.amount)}
              {item.includes_iva && <span className="text-muted ml-1">+ ${fmtRaw(item.iva_amount)}</span>}
            </span>
            <div className="d-flex" style={{ gap: '4px' }}>
              {categoryItems && (
                <>
                  <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                    onClick={() => handleMoveItem(categoryItems, item.id, 'up')} title="Subir">
                    <i className="ti-arrow-up"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                    onClick={() => handleMoveItem(categoryItems, item.id, 'down')} title="Bajar">
                    <i className="ti-arrow-down"></i>
                  </button>
                </>
              )}
              <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                onClick={() => handleEditItem(item)}>
                <i className="ti-pencil"></i>
              </button>
              <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }}
                onClick={() => setDeleteItemModal(item)}>
                <i className="ti-trash"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Presupuestos', to: '/budgets' }, { label: budget.folio || `#${budget.id}` }]} />

      <ConfirmModal
        show={!!deleteItemModal}
        title="Eliminar Concepto"
        message={`¿Eliminar "${deleteItemModal?.name}" del presupuesto?`}
        confirmLabel="Eliminar"
        onConfirm={handleDeleteItem}
        onCancel={() => setDeleteItemModal(null)}
        loading={deletingItem}
      />
      <ConfirmModal
        show={!!deletePaymentModal}
        title="Eliminar Pago"
        message={`¿Eliminar el pago de $${fmtRaw(deletePaymentModal?.amount || 0)} registrado el ${deletePaymentModal?.payment_date?.slice(0, 10) || ''}?`}
        confirmLabel="Eliminar"
        onConfirm={handleDeletePayment}
        onCancel={() => setDeletePaymentModal(null)}
        loading={deletingPayment}
      />

      {/* Header */}
      <div className="page-header d-flex justify-content-between align-items-start mb-4 flex-wrap" style={{ gap: '10px' }}>
        <div>
          <h4 className="mb-0">{budget.folio || `Presupuesto #${budget.id}`}</h4>
          <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: '8px' }}>
            <span className={`badge ${statusLabels[budget.status]?.color}`}>{statusLabels[budget.status]?.label}</span>
            <select className="form-control form-control-sm" style={{ width: 'auto' }}
              value={budget.status}
              onChange={async (e) => {
                await api.put(`/budgets/${id}`, { status: e.target.value });
                toast.success('Estado actualizado');
                fetchBudget();
              }}>
              <option value="draft">Borrador</option>
              <option value="sent">Enviado</option>
              <option value="accepted">Aceptado</option>
              <option value="cancelled">Cancelado</option>
            </select>
          </div>
          <small className="text-muted">
            {budget.document_type?.name}
            {budget.case_file && <> · <Link to={`/clients/${budget.case_file.client_id}`}>{budget.case_file.client?.name}</Link></>}
          </small>
        </div>
        <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowPdfMenu(prev => !prev)}>
              <i className="ti-printer mr-1"></i> PDF ▾
            </button>
            {showPdfMenu && (
              <>
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setShowPdfMenu(false)} />
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  backgroundColor: '#fff', border: '1px solid #dee2e6',
                  borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000, minWidth: 170,
                }}>
                  <button className="dropdown-item" onClick={() => { handlePrint('default'); setShowPdfMenu(false); }}>
                    <i className="ti-file mr-2 text-muted"></i> Estilo Clásico
                  </button>
                  <button className="dropdown-item" onClick={() => { handlePrint('elegant'); setShowPdfMenu(false); }}>
                    <i className="ti-star mr-2 text-warning"></i> Estilo Elegante
                  </button>
                </div>
              </>
            )}
          </div>
          <Link to="/budgets" className="btn btn-outline-secondary btn-sm">
            <i className="ti-arrow-left mr-1"></i> Regresar
          </Link>
        </div>
      </div>

      <div className="row">
        {/* Panel lateral */}
        <div className="col-12 col-md-3 mb-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Información</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingInfo(!editingInfo)}>
                <i className={`ti-${editingInfo ? 'close' : 'pencil'}`}></i>
              </button>
            </div>
            <div className="card-body">
              {editingInfo ? (
                <>
                  <div className="form-group">
                    <label className="small">Idioma</label>
                    <select className="form-control form-control-sm" value={infoForm.language}
                      onChange={e => setInfoForm({ ...infoForm, language: e.target.value })}>
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="small">Moneda</label>
                    <select className="form-control form-control-sm" value={infoForm.currency}
                      onChange={e => setInfoForm({ ...infoForm, currency: e.target.value })}>
                      <option value="mxn">MXN</option>
                      <option value="usd">USD</option>
                    </select>
                  </div>
                  {infoForm.currency === 'usd' && (
                    <div className="form-group">
                      <label className="small">Tipo de cambio</label>
                      <input type="number" className="form-control form-control-sm" step="0.0001"
                        value={infoForm.usd_exchange_rate}
                        onChange={e => setInfoForm({ ...infoForm, usd_exchange_rate: e.target.value })} />
                    </div>
                  )}
                  {!budget.case_file && (
                    <>
                      <div className="form-group">
                        <label className="small">Nombre cliente</label>
                        <input type="text" className="form-control form-control-sm"
                          value={infoForm.client_name} onChange={e => setInfoForm({ ...infoForm, client_name: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="small">Teléfono</label>
                        <input type="text" className="form-control form-control-sm"
                          value={infoForm.client_phone} onChange={e => setInfoForm({ ...infoForm, client_phone: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="small">Correo</label>
                        <input type="email" className="form-control form-control-sm"
                          value={infoForm.client_email} onChange={e => setInfoForm({ ...infoForm, client_email: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="small">Dirección</label>
                        <input type="text" className="form-control form-control-sm"
                          value={infoForm.client_address} onChange={e => setInfoForm({ ...infoForm, client_address: e.target.value })} />
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label className="small">Responsable</label>
                    <input type="text" className="form-control form-control-sm"
                      value={infoForm.responsible_name} onChange={e => setInfoForm({ ...infoForm, responsible_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="small">Notas</label>
                    <textarea className="form-control form-control-sm" rows="2"
                      value={infoForm.notes} onChange={e => setInfoForm({ ...infoForm, notes: e.target.value })}></textarea>
                  </div>
                  <div className="custom-control custom-checkbox mb-3">
                    <input type="checkbox" className="custom-control-input" id="showBankAcc"
                      checked={infoForm.show_bank_accounts}
                      onChange={e => setInfoForm({ ...infoForm, show_bank_accounts: e.target.checked })} />
                    <label className="custom-control-label small" htmlFor="showBankAcc">Mostrar cuentas bancarias</label>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveInfo} disabled={savingInfo}>
                      {savingInfo ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingInfo(false)}>Cancelar</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <span className="badge badge-secondary">{budget.language === 'es' ? 'ES' : 'EN'}</span>
                    <span className="badge badge-secondary ml-1">{budget.currency?.toUpperCase()}</span>
                  </div>
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted small">Cliente</td>
                        <td className="small">
                          {budget.case_file
                            ? <Link to={`/clients/${budget.case_file.client_id}`}>{budget.case_file.client?.name}</Link>
                            : budget.client_name || 'Público en general'}
                        </td>
                      </tr>
                      {budget.responsible_name && (
                        <tr><td className="text-muted small">Responsable</td><td className="small">{budget.responsible_name}</td></tr>
                      )}
                      {budget.currency === 'usd' && (
                        <tr><td className="text-muted small">T/C</td><td className="small">${budget.usd_exchange_rate} MXN/USD</td></tr>
                      )}
                      <tr><td className="text-muted small">Creado</td><td className="small">{new Date(budget.created_at).toLocaleDateString('es-MX')}</td></tr>
                      <tr><td className="text-muted small">Por</td><td className="small">{budget.created_by?.name}</td></tr>
                    </tbody>
                  </table>
                  {budget.notes && <><hr /><p className="small mb-0">{budget.notes}</p></>}
                </>
              )}
            </div>
          </div>

          {/* Resumen totales */}
          <div className="card mt-3">
            <div className="card-header"><h6 className="mb-0">Resumen</h6></div>
            <div className="card-body p-0">
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  {Object.entries(totals.subtotals || {}).map(([catId, subtotal]) => {
                    const cat = categories.find(c => String(c.id) === String(catId));
                    return cat ? (
                      <tr key={catId}>
                        <td className="text-muted small">{cat.name}</td>
                        <td className="small text-right">{fmt(subtotal)}</td>
                      </tr>
                    ) : null;
                  })}
                  {discountItems.length > 0 && (
                    <tr>
                      <td className="text-danger small">Descuentos</td>
                      <td className="small text-right text-danger">- {fmt(totals.total_discounts || 0)}</td>
                    </tr>
                  )}
                  <tr style={{ borderTop: '2px solid #dee2e6' }}>
                    <td className="font-weight-bold small">TOTAL</td>
                    <td className="font-weight-bold small text-right text-primary">{fmt(grandTotal)}</td>
                  </tr>
                  {totalPaid > 0 && (
                    <tr>
                      <td className="text-muted small">Pagado</td>
                      <td className="small text-right text-success">- {fmt(totalPaid)}</td>
                    </tr>
                  )}
                  {balance > 0 && totalPaid > 0 && (
                    <tr>
                      <td className="text-danger small font-weight-bold">Saldo</td>
                      <td className="small text-right text-danger font-weight-bold">{fmt(balance)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="col-12 col-md-9">
          <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <ul className="nav nav-tabs mb-0 flex-nowrap">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'items' ? 'active' : ''}`} onClick={() => setActiveTab('items')}>
                  Conceptos <span className="badge badge-secondary ml-1">{allItems.length}</span>
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => setActiveTab('payments')}>
                  Pagos <span className="badge badge-secondary ml-1">{budget.payments?.length || 0}</span>
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'versions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('versions')}
                  style={{ position: 'relative' }}
                >
                  <i className="ti-time mr-1"></i>
                  Versiones
                  {hasUnversionedChanges && (
                    <span style={{
                      position: 'absolute', top: 6, right: 4,
                      width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: '#F59E0B',
                      border: '1.5px solid #fff',
                    }} />
                  )}
                </button>
              </li>
                {user?.role === 'notary' && (
                <li className="nav-item">
                  <button className={`nav-link ${activeTab === 'audit' ? 'active' : ''}`}
                    onClick={() => setActiveTab('audit')}>
                    Auditoría <span className="badge badge-secondary ml-1">{auditLog.length}</span>
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div className="card" style={{ borderTopLeftRadius: 0 }}>

            {/* ── TAB CONCEPTOS ── */}
            {activeTab === 'items' && (
              <div className="card-body">
                {informativeItems.length > 0 && (
                  <div className="mb-4">
                    <p className="text-muted mb-2" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      Valores informativos
                    </p>
                    <div className="border rounded" style={{ overflowX: 'auto' }}>
                      <table className="table table-sm mb-0">
                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                          <tr>
                            {informativeItems.map(item => (
                              <th key={item.id} className="small text-center">
                                {budget.language === 'en' && item.name_en ? item.name_en : item.name}
                              </th>
                            ))}
                            <th style={{ width: 50 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {informativeItems.map(item => (
                              <td key={item.id} className="text-center">
                                {editingItem === item.id ? (
                                  <div className="d-flex justify-content-center" style={{ gap: '4px' }}>
                                    <input type="number" className="form-control form-control-sm" min="0" step="0.01"
                                      value={itemForm.amount}
                                      onChange={e => setItemForm({ ...itemForm, amount: e.target.value })}
                                      style={{ width: 110 }} />
                                    <button className="btn btn-primary btn-sm" onClick={() => handleSaveItem(item.id)} disabled={savingItem}>✓</button>
                                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditingItem(null)}>✕</button>
                                  </div>
                                ) : (
                                  <span className="small font-weight-bold">{fmt(item.amount)}</span>
                                )}
                              </td>
                            ))}
                            <td className="text-center">
                              {informativeItems.map(item => (
                                editingItem !== item.id && (
                                  <button key={item.id} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                                    onClick={() => handleEditItem(item)}>
                                    <i className="ti-pencil"></i>
                                  </button>
                                )
                              ))}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {allItems.length === 0 && (
                  <div className="text-center text-muted py-4">Sin conceptos en este presupuesto</div>
                )}

                {categories.map(cat => (
                  <div key={cat.id} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 font-weight-bold">
                        {budget.language === 'en' && cat.name_en ? cat.name_en : cat.name}
                      </h6>
                      <button className="btn btn-outline-primary btn-sm"
                        onClick={() => { setShowAddItem(cat.id); setNewItemForm({ name: '', name_en: '', type: 'normal', amount: 0, includes_iva: false, iva_rate: 16, budget_concept_category_id: cat.id }); }}>
                        <i className="ti-plus mr-1"></i> Agregar
                      </button>
                    </div>

                    {showAddItem === cat.id && (
                      <div className="border rounded p-3 mb-3 bg-light">
                        <form onSubmit={e => handleAddItem(e, cat.id)}>
                          <div className="row">
                            <div className="col-12 col-md-5">
                              <div className="form-group mb-2">
                                <input type="text" className="form-control form-control-sm" placeholder="Nombre ES *"
                                  value={newItemForm.name} onChange={e => setNewItemForm({ ...newItemForm, name: e.target.value })} required />
                              </div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="form-group mb-2">
                                <input type="text" className="form-control form-control-sm" placeholder="Nombre EN"
                                  value={newItemForm.name_en} onChange={e => setNewItemForm({ ...newItemForm, name_en: e.target.value })} />
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="form-group mb-2">
                                <select className="form-control form-control-sm" value={newItemForm.type}
                                  onChange={e => setNewItemForm({ ...newItemForm, type: e.target.value })}>
                                  <option value="normal">Normal</option>
                                  <option value="informative">Informativo</option>
                                  <option value="discount">Descuento</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="form-group mb-2">
                                <input type="number" className="form-control form-control-sm" placeholder="Monto" min="0" step="0.01"
                                  value={newItemForm.amount} onChange={e => setNewItemForm({ ...newItemForm, amount: e.target.value })} />
                              </div>
                            </div>
                            <div className="col-6 col-md-3">
                              <div className="custom-control custom-checkbox mt-1">
                                <input type="checkbox" className="custom-control-input" id={`newiva-${cat.id}`}
                                  checked={newItemForm.includes_iva}
                                  onChange={e => setNewItemForm({ ...newItemForm, includes_iva: e.target.checked })} />
                                <label className="custom-control-label small" htmlFor={`newiva-${cat.id}`}>IVA</label>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex" style={{ gap: '6px' }}>
                            <button type="submit" className="btn btn-primary btn-sm">Agregar</button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddItem(null)}>Cancelar</button>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="border rounded">
                      {cat.items.map(item => renderItemRow(item, cat.items))}
                      <div className="d-flex justify-content-between p-2" style={{ backgroundColor: '#f8f9fa' }}>
                        <small className="text-muted font-weight-bold">Subtotal</small>
                        <small className="font-weight-bold">{fmt(totals.subtotals?.[cat.id] || 0)}</small>
                      </div>
                    </div>
                  </div>
                ))}

                {discountItems.length > 0 && (
                  <div className="mb-4">
                    <h6 className="mb-2 font-weight-bold text-danger">Descuentos</h6>
                    <div className="border rounded" style={{ borderColor: '#fca5a5' }}>
                      {discountItems.map(item => renderItemRow(item, null))}
                      <div className="d-flex justify-content-between p-2" style={{ backgroundColor: '#FEF2F2' }}>
                        <small className="text-danger font-weight-bold">Total descuentos</small>
                        <small className="text-danger font-weight-bold">- {fmt(totals.total_discounts || 0)}</small>
                      </div>
                    </div>
                  </div>
                )}

                {(categories.length > 0 || discountItems.length > 0) && (
                  <div className="border rounded p-3" style={{ backgroundColor: '#EEF0FB' }}>
                    <div className="d-flex justify-content-between">
                      <strong>TOTAL</strong>
                      <strong className="text-primary" style={{ fontSize: '18px' }}>{fmt(grandTotal)}</strong>
                    </div>
                    {balance > 0 && totalPaid > 0 && (
                      <div className="d-flex justify-content-between mt-1">
                        <small className="text-danger">Saldo pendiente</small>
                        <small className="text-danger font-weight-bold">{fmt(balance)}</small>
                      </div>
                    )}
                  </div>
                )}
                {hasUnversionedChanges && (
                  <div
                    className="d-flex justify-content-between align-items-center mt-3 px-3 py-2 rounded"
                    style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}
                  >
                    <div style={{ fontSize: 13, color: '#92400E' }}>
                      <strong>🕐 Cambios sin versionar</strong>
                      <span className="ml-2">— guarda una versión para registrar estos cambios en el historial.</span>
                    </div>
                    <button
                      className="btn btn-sm btn-warning ml-3"
                      style={{ flexShrink: 0 }}
                      onClick={() => setActiveTab('versions')}
                    >
                      Guardar versión
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── TAB PAGOS ── */}
            {activeTab === 'payments' && (
              <>
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Pagos</h6>
                  {!showPaymentForm && !editingPayment && (
                    <button className="btn btn-primary btn-sm" onClick={() => { setShowPaymentForm(true); setPaymentForm(emptyPayment); setPaymentVoucher(null); }}>
                      <i className="ti-plus mr-1"></i> Registrar Pago
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {showPaymentForm && paymentFormJSX(() => {
                    setShowPaymentForm(false);
                    setPaymentForm(emptyPayment);
                    setPaymentVoucher(null);
                  })}

                  {budget.payments?.length === 0 && !showPaymentForm && (
                    <div className="text-center text-muted py-4">No hay pagos registrados</div>
                  )}

                  {budget.payments?.map(payment => (
                    <div key={payment.id} className="border rounded mb-3">
                      {editingPayment === payment.id && (
                        <div className="p-3">
                          {paymentFormJSX(() => {
                            setEditingPayment(null);
                            setPaymentForm(emptyPayment);
                            setPaymentVoucher(null);
                          })}
                        </div>
                      )}
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: '8px' }}>
                          <div>
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
                              <span className={`badge ${paymentStatusLabels[payment.status]?.color}`}>
                                {paymentStatusLabels[payment.status]?.label}
                              </span>
                              <strong>${fmtRaw(payment.amount)} {payment.currency?.toUpperCase()}</strong>
                              <small className="text-muted">{paymentMethodLabels[payment.payment_method]}</small>
                            </div>
                            <div className="small text-muted mt-1">
                              {new Date(payment.payment_date).toLocaleDateString('es-MX')}
                              {payment.reference && ` · Ref: ${payment.reference}`}
                              {' · '}Registrado por: {payment.registered_by?.name}
                            </div>
                            {payment.notes && <div className="small text-muted mt-1">{payment.notes}</div>}
                            {payment.validation_notes && (
                              <div className="small text-muted mt-1">Nota validación: {payment.validation_notes}</div>
                            )}
                            {(payment.voucher_drive_url || payment.voucher_path) && (
                              <a href={payment.voucher_drive_url || `${STORAGE_URL}/${payment.voucher_path}`}
                                target="_blank" rel="noreferrer"
                                className="btn btn-sm btn-outline-secondary mt-1" style={{ fontSize: '11px' }}>
                                <i className={`ti-${payment.voucher_drive_url ? 'google' : 'file'} mr-1`}></i>
                                {payment.voucher_drive_url ? 'Ver en Drive' : 'Ver comprobante'}
                              </a>
                            )}
                          </div>
                          <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                            {isNotary && payment.status === 'pending' && (
                              <>
                                <button className="btn btn-sm btn-success"
                                  onClick={() => handleValidatePayment(payment.id, 'validated')}>
                                  <i className="ti-check mr-1"></i> Validar
                                </button>
                                <button className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleValidatePayment(payment.id, 'rejected')}>
                                  Rechazar
                                </button>
                              </>
                            )}
                            {(isNotary || payment.registered_by?.id === user?.id) && payment.status !== 'validated' && (
                              <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleEditPayment(payment)}>
                                <i className="ti-pencil"></i>
                              </button>
                            )}
                            {isNotary && payment.status === 'validated' && (
                              <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleEditPayment(payment)}
                                title="Editar (volverá a pendiente)">
                                <i className="ti-pencil"></i>
                              </button>
                            )}
                            {(isNotary || payment.registered_by?.id === user?.id) && (
                              <button className="btn btn-sm btn-outline-danger"
                                onClick={() => setDeletePaymentModal(payment)}>
                                <i className="ti-trash"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(budget.payments?.length > 0 || totalPaid > 0) && (
                    <div className="border rounded p-3 mt-3" style={{ backgroundColor: '#f8f9fa' }}>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Total del presupuesto</small>
                        <small className="font-weight-bold">{fmt(grandTotal)}</small>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="text-muted">Total pagado (validado)</small>
                        <small className="text-success font-weight-bold">{fmt(totalPaid)}</small>
                      </div>
                      <div className="d-flex justify-content-between border-top pt-1 mt-1">
                        <small className={balance > 0 ? 'text-danger font-weight-bold' : 'text-success font-weight-bold'}>
                          {balance > 0 ? 'Saldo pendiente' : '✓ Pagado completo'}
                        </small>
                        <small className={`font-weight-bold ${balance > 0 ? 'text-danger' : 'text-success'}`}>
                          {balance > 0 ? fmt(balance) : fmt(0)}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── TAB VERSIONES ── */}
            {activeTab === 'versions' && (
              <div className="card-body">
                <BudgetVersions
                  key={versionsKey}
                  budgetId={id}
                  isNotary={isNotary}
                  onRestored={handleVersionRestored}
                  onVersionSaved={() => setHasUnversionedChanges(false)}
                />
              </div>
            )}

            {/* ── TAB AUDITORÍA ── */}
            {activeTab === 'audit' && user?.role === 'notary' && (
              <>
                <div className="card-header"><h6 className="mb-0">Log de Auditoría</h6></div>
                <div className="card-body p-0" style={{ overflowX: 'auto' }}>
                  {loadingAudit ? (
                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                  ) : auditLog.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay registros de auditoría</div>
                  ) : (
                    <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '12%' }}>Acción</th>
                          <th style={{ width: '18%' }}>Por</th>
                          <th style={{ width: '40%' }}>Cambios</th>
                          <th style={{ width: '18%' }}>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.map(log => (
                          <tr key={log.id}>
                            <td>
                              <span className={`badge ${log.event === 'created' ? 'badge-success' : log.event === 'deleted' ? 'badge-danger' : 'badge-info'}`}>
                                {log.event === 'created' ? 'Creado' : log.event === 'deleted' ? 'Eliminado' : 'Editado'}
                              </span>
                            </td>
                            <td><small>{log.causer}</small></td>
                            <td style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                              {log.old && log.new ? (
                                <div style={{ fontSize: '11px' }}>
                                  {Object.keys(log.new).map(key => (
                                    log.old[key] !== log.new[key] ? (
                                      <div key={key} className="mb-1">
                                        <span className="text-muted">{key}:</span>{' '}
                                        <span className="text-danger" style={{ textDecoration: 'line-through' }}>{String(log.old[key] ?? '—')}</span>
                                        {' → '}
                                        <span className="text-success">{String(log.new[key] ?? '—')}</span>
                                      </div>
                                    ) : null
                                  ))}
                                </div>
                              ) : (
                                <small className="text-muted">{log.description}</small>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(log.created_at).toLocaleDateString('es-MX', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetDetail;